// @noflow
import { gql } from '@apollo/client'

/*
 * This query is used to fetch the initial data needed for the client to render the app and for Access Management.
 *
 * Please DO NOT add any additional fields to query here unless:
 * 1. They are absolutely necessary to render the app (this does not include modals that appear on initial load)
 * 2. They are needed for Access Management
 * 3. They are a commonly queried field that will be used in multiple places
 *
 * If you feel like you need to add a field to this query and it doesn't meet the above requirements, please raise
 * it in the tech-front-end slack channel.
 *
 * (Feature flags that are currently being used in the app are an exception to this rule, new flags
 * should be queried for when they are needed from now on unless they meet the above requirements)
 */

const CLIENT_INIT_QUERY = gql`
  query ClientInitQuery($archivedType: ArchivedType!) {
    systemShouldShowAppContent: featureFlag(
      flag: "system_should_show_app_content"
      fallback: "not_in_cohort"
    )
    shouldSeeXmasTheme: featureFlag(
      flag: "should_see_xmas_theme"
      fallback: "false"
    )
    shouldSeeOnlyMoveThisBoxCheckbox: featureFlag(
      flag: "should-see-move-only-this-box-checkbox"
      fallback: "false"
    )
    shouldSeePlanManagementPhase3: featureFlag(
      flag: "should_see_plan_management_phase_3"
      fallback: "not_in_cohort"
    )
    shouldTrackErrorsOnErrorState: featureFlag(
      flag: "should_track_errors_on_error_state"
      fallback: "false"
    )
    shouldSeeUpcomingPriceRiseInfo: featureFlag(
      flag: "system-should-see-upcoming-price-rise-info"
      fallback: "false"
    )
    shouldSeeSelfResolutions: featureFlag(
      flag: "should_see_self_resolutions"
      fallback: "false"
    )
    shouldSeeStap: featureFlag(flag: "should_see_stap", fallback: "false")
    shouldSeeSwapToMFITBPlanOfferPage: featureFlag(
      flag: "should_see_swap_to_mfitb_plan_offer_page"
      fallback: "not_in_cohort"
    )
    shouldSeeThankYouPagev3: featureFlag(
      flag: "should_see_thank_you_page_v3"
      fallback: "false"
    )
    shouldSeeIntercom: featureFlag(
      flag: "should_see_intercom"
      fallback: "false"
    )
    shouldSeePriceSensitivePausedPage: featureFlag(
      flag: "should_see_price_sensitive_paused_page"
      fallback: "not_in_cohort"
    )
    settings(
      names: [
        "carrier_preferences_enabled"
        "should_see_sms_delivery_reminders_section"
      ]
    ) {
      name
      value
    }
    shouldSeeSegmentedPausedPage: featureFlag(
      flag: "should_see_segmented_paused_page"
      fallback: "not_in_cohort"
    )
    shouldSeeNextDayDeliveryAvailableOnReactivation: featureFlag(
      flag: "should-see-next-day-delivery-available-on-reactivation"
      fallback: "not_in_cohort"
    )
    notifications {
      id
    }
    currentAdminUser {
      id
    }
    nonCoreOrderDeliveryFee
    nonCoreOrderSmallOrderFee
    nonCoreOrderSmallOrderMinimumOrderValue
    user {
      id
      firstName
      affiliateType
      dogs {
        id
        name
        gender
        ageInMonths
        eaterType
      }
      subscription {
        id
        status
        deliveriesReceived
        increasedDeliveryCadencePlan {
          id
        }
        plan {
          id
          durationInWeeks
          numberOfPouches
          pouchSize
          swappablePlan {
            id
          }
          downsizeablePlan {
            id
          }
        }
        paymentMethods(archivedType: $archivedType) {
          ... on CreditCardPaymentMethod {
            id
            active
          }
          ... on BillingAgreement {
            id
            active
          }
          ... on MerchantInitiatedPayment {
            id
            active
          }
          ... on SepaDirectDebit {
            id
            active
          }
        }
      }
      shippingCountry {
        id
        code
        showExtras
        showCare
        defaultLanguage
      }
      preferredLanguage
    }
  }
`

export { CLIENT_INIT_QUERY }
